import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Badge,
  Progress,
  Icon,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import { FiTarget, FiBook, FiHelpCircle, FiTrendingUp, FiCompass } from 'react-icons/fi';
import { useDebugStore } from '../../../../stores/debugStore';
import { useWebSocketDebug } from '../hooks/useWebSocketDebug';

const TeachingStrategyIndicator: React.FC = () => {
  const { teachingStrategy } = useDebugStore();
  const { simulatePromptGeneration } = useWebSocketDebug();
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  const getStrategyDetails = (strategy?: string) => {
    switch (strategy) {
      case 'remedial':
        return {
          icon: FiHelpCircle,
          color: 'orange',
          label: 'Remedial Support',
          description: 'Providing foundational help and simplified explanations',
        };
      case 'adaptive':
        return {
          icon: FiBook,
          color: 'blue',
          label: 'Adaptive Learning',
          description: 'Personalized teaching based on current skill level',
        };
      case 'socratic':
        return {
          icon: FiTarget,
          color: 'purple',
          label: 'Socratic Method',
          description: 'Guiding through questions to promote critical thinking',
        };
      case 'challenge':
        return {
          icon: FiTrendingUp,
          color: 'green',
          label: 'Challenge Mode',
          description: 'Advanced problems and enrichment activities',
        };
      case 'exploratory':
        return {
          icon: FiCompass,
          color: 'teal',
          label: 'Exploratory Learning',
          description: 'Open-ended investigation and discovery',
        };
      default:
        return {
          icon: FiBook,
          color: 'gray',
          label: 'Unknown Strategy',
          description: 'Teaching strategy not determined',
        };
    }
  };

  const strategyDetails = getStrategyDetails(teachingStrategy?.current);

  return (
    <VStack align="stretch" spacing={4}>
      <Heading size="md">Teaching Strategy</Heading>

      {!teachingStrategy ? (
        <Box
          p={8}
          textAlign="center"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          borderStyle="dashed"
          cursor="pointer"
          onClick={simulatePromptGeneration}
          _hover={{ borderColor: 'blue.300' }}
        >
          <Text color="gray.500">No strategy selected yet</Text>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Click to simulate
          </Text>
        </Box>
      ) : (
        <Box
          p={4}
          bg={cardBg}
          borderRadius="lg"
          borderWidth={1}
          borderColor={`${strategyDetails.color}.200`}
        >
          <VStack align="stretch" spacing={3}>
            <HStack spacing={3}>
              <Box
                p={3}
                bg={`${strategyDetails.color}.100`}
                borderRadius="md"
              >
                <Icon
                  as={strategyDetails.icon}
                  boxSize={6}
                  color={`${strategyDetails.color}.600`}
                />
              </Box>
              <VStack align="start" spacing={0} flex={1}>
                <Text fontWeight="bold" fontSize="lg">
                  {strategyDetails.label}
                </Text>
                <Badge
                  colorScheme={strategyDetails.color}
                  variant="subtle"
                  fontSize="xs"
                >
                  {teachingStrategy.current?.toUpperCase()}
                </Badge>
              </VStack>
            </HStack>

            <Text fontSize="sm" color="gray.600">
              {strategyDetails.description}
            </Text>

            {teachingStrategy.reason && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
                  Reasoning:
                </Text>
                <Text fontSize="sm">{teachingStrategy.reason}</Text>
              </Box>
            )}

            {teachingStrategy.confidence !== undefined && (
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="xs" color="gray.600">Confidence</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {Math.round(teachingStrategy.confidence * 100)}%
                  </Text>
                </HStack>
                <Tooltip
                  label={`System is ${Math.round(teachingStrategy.confidence * 100)}% confident in this strategy choice`}
                >
                  <Progress
                    value={teachingStrategy.confidence * 100}
                    size="sm"
                    colorScheme={strategyDetails.color}
                    borderRadius="full"
                  />
                </Tooltip>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default TeachingStrategyIndicator;